import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "./cn";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

// 닫힘은 퇴장 애니메이션 없이 hidden — Presence 가 animationend 를 기다리며 닫힌 층을 남기지 않게(alert-dialog·sheet 동일).
const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=closed]:hidden data-[state=open]:animate-in data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

/**
 * 원형 배경이 있는 닫기 버튼 — DialogContent 기본 X 와 동일한 모양.
 * 바깥 버튼(44px)은 터치 타겟, 안쪽 원(36px)이 눈에 보이는 영역이다.
 * 기본 X 는 스크롤 컨테이너(DialogContent)에 absolute 라 내용과 함께 흘러가므로,
 * 스크롤이 긴 모달은 isCloseIcon={false} 로 끄고 sticky 헤더 안에 이걸 넣는다.
 */
const DialogCloseButton = ({ className }: { className?: string }) => (
  <DialogPrimitive.Close
    className={cn(
      "group absolute right-2 top-2 z-20 flex size-11 items-center justify-center rounded-full focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none",
      className
    )}>
    <span className="flex size-9 items-center justify-center rounded-full bg-background/80 text-foreground shadow-xs backdrop-blur-xs transition-colors group-hover:bg-background">
      <X className="size-5" />
    </span>
    <span className="sr-only">닫기</span>
  </DialogPrimitive.Close>
);

// 본문 안에 DialogDescription 이 있는지 — 있으면 Radix 기본(설명 id)을 두고, 없으면 aria-describedby 를 뺀다.
const DescriptionSlot = React.createContext<((delta: number) => void) | null>(null);

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    isCloseIcon?: boolean;
  }
>(({ className, children, isCloseIcon = true, ...props }, ref) => {
  const [descriptions, setDescriptions] = React.useState(0);
  const count = React.useCallback((delta: number) => setDescriptions((n) => n + delta), []);
  const describedBy = "aria-describedby" in props || descriptions > 0 ? {} : { "aria-describedby": undefined };
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        {...describedBy}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 grid max-h-full w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 overflow-auto border bg-background-dialog p-6 shadow-lg duration-200 data-[state=closed]:hidden data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:rounded-lg",
          className
        )}
        {...props}>
        <DescriptionSlot.Provider value={count}>{children}</DescriptionSlot.Provider>
        {/* z-20 필수 — 본문에 sticky 헤더처럼 z-index 가 붙은 요소가 있으면
            z-index:auto 인 닫기 버튼이 그 아래로 깔려 눌리지 않는다(모달이 화면을
            꽉 채우는 모바일에서는 백드롭도 못 눌러 갇힌다). */}
        {isCloseIcon && <DialogCloseButton />}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => {
  const register = React.useContext(DescriptionSlot);
  React.useLayoutEffect(() => {
    register?.(1);
    return () => register?.(-1);
  }, [register]);
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
});
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
